import React, { useEffect, useMemo, useState } from 'react';
import { getSeatUIState, getGlobalUIState } from '../../../utils/phaseState';
import { getSeatSlot, getHandAnchor, getHandResultSummary } from '../../../utils/casinoSeatLayout';
import PhaseControls from '../../hud/PhaseControls';
import TableShell from './TableShell';
import CenterStage from './CenterStage';
import PlayerStation from './PlayerStation';
import PlayingCard, { LocalHand, OpponentHand } from './PlayingCard';
import HandHistoryPanel from './HandHistoryPanel';
import PaymentOwedPanel from './PaymentOwedPanel';
import TableUtilityOverlay from './TableUtilityOverlay';
import '../../../styles/casinoTable.css';

/**
 * Gutz v2 casino presentation shell.
 * Reuses phaseState + PhaseControls socket wiring from the existing modular path.
 * Does not alter game rules — presentation only.
 */
export default function TableCasino({ table, player, socket, onTakeBreak, onQuit }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [utilityPanel, setUtilityPanel] = useState(null); // null | 'history' | 'payment'
  // Active rotation only — matches server hands/knocked/activePlayerIdx indices.
  const activeSeats = useMemo(() => (table?.players || []).concat(table?.bots || []), [table]);
  // Pending late joiners appended after bots so current-hand indices stay stable.
  const pendingPlayers = table?.pendingPlayers || [];
  const allSeats = useMemo(() => activeSeats.concat(pendingPlayers), [activeSeats, pendingPlayers]);
  const state = useMemo(() => table?.state ?? {}, [table?.state]);
  const historyArr = state.history || [];
  const paymentHistory = state.paymentHistory || [];
  const creditValue = state.creditValue || 1;
  const myIdx = activeSeats.findIndex((p) => p.playerID === player?.playerID);
  const localPendingPlayer = pendingPlayers.find((p) => p.playerID === player?.playerID) || null;
  const myDisplayIdx = allSeats.findIndex((p) => p.playerID === player?.playerID);

  const globalUIState = useMemo(
    () =>
      getGlobalUIState({
        phase: state.phase,
        gameState: state,
        localPlayerID: player?.playerID,
        allSeats: activeSeats,
        localPendingPlayer,
      }),
    [state, player?.playerID, activeSeats, localPendingPlayer]
  );

  // Authoritative phase logging — helps confirm clients exit knock/discard with the server.
  useEffect(() => {
    if (!state?.phase) return;
    console.log('[cv2-phase]', {
      phase: state.phase,
      activePlayerIdx: state.activePlayerIdx,
      localIdx: myIdx,
      pending: !!localPendingPlayer,
      knocked: state.knocked,
      discarded: state.discarded,
      winnerIdx: state.winnerIdx,
      showKnockDiscard: !!globalUIState?.controls?.showKnockDiscard,
    });
  }, [
    state.phase,
    state.activePlayerIdx,
    state.knocked,
    state.discarded,
    state.winnerIdx,
    myIdx,
    localPendingPlayer,
    globalUIState?.controls?.showKnockDiscard,
  ]);

  const seatData = useMemo(() => {
    return allSeats.map((p, displayIdx) => {
      const isPending = !!(p.pendingJoin || p.inactiveUntilHand);
      const activeIdx = isPending ? -1 : activeSeats.findIndex((a) => a.playerID === p.playerID);
      const slot = getSeatSlot(displayIdx, allSeats.length, myDisplayIdx >= 0 ? myDisplayIdx : 0);
      const seatState = getSeatUIState({
        seatIndex: activeIdx >= 0 ? activeIdx : displayIdx,
        player: p,
        phase: state.phase,
        gameState: state,
        localPlayerID: player?.playerID,
      });
      return { slot, seatState, idx: activeIdx, displayIdx, isPending };
    });
  }, [allSeats, activeSeats, myDisplayIdx, state, player?.playerID]);

  const handResult = useMemo(
    () => getHandResultSummary(state, activeSeats),
    [state, activeSeats]
  );

  if (!table || !player) {
    return (
      <div className="cv2-root" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="cv2-phase-pill">Loading table…</div>
      </div>
    );
  }

  return (
    <div className="cv2-root">
      <div className="cv2-vignette" aria-hidden="true" />

      <header className="cv2-chrome">
        <div>
          <div className="cv2-chrome-title">GUTZ</div>
          <div className="cv2-chrome-meta">
            Table {String(table.id || '').slice(0, 6)} · {allSeats.length} seated
          </div>
        </div>
        <div className="cv2-chrome-actions">
          <button
            type="button"
            className="cv2-btn ghost cv2-chrome-util"
            onClick={() => setUtilityPanel('history')}
            aria-label="Hand History"
          >
            <span className="cv2-chrome-util-long">Hand History</span>
            <span className="cv2-chrome-util-short">History</span>
          </button>
          <button
            type="button"
            className="cv2-btn ghost cv2-chrome-util"
            onClick={() => setUtilityPanel('payment')}
            aria-label="Payment Owed"
          >
            <span className="cv2-chrome-util-long">Payment Owed</span>
            <span className="cv2-chrome-util-short">Payments</span>
          </button>
          <button type="button" className="cv2-btn ghost" onClick={() => setMenuOpen(true)}>
            Menu
          </button>
        </div>
      </header>

      <TableShell seatCount={allSeats.length}>
        <CenterStage
          pot={state.pot || 0}
          creditValue={state.creditValue || 1}
          phase={state.phase}
          result={handResult}
        />

        {seatData.map(({ slot, seatState, idx }) => {
          const vote = state.suitVotes?.find((v) => v.playerID === seatState.player.playerID)?.suit || null;
          const free = !!(state.freeHands && state.freeHands[seatState.player.playerID]);
          return (
            <PlayerStation
              key={seatState.player.playerID || idx}
              seatState={seatState}
              slot={slot}
              creditValue={state.creditValue || 1}
              suitVote={vote}
              phase={state.phase}
              hasFreeHand={free}
              winnerEarnings={seatState.isWinner ? (state.winnerEarnings || 0) : 0}
            />
          );
        })}

        {/* Hands during action / reveal / results — active seats only */}
        {(state.phase === 'action' || state.phase === 'reveal' || state.phase === 'results') &&
          seatData.map(({ slot, seatState, idx, isPending }) => {
            if (isPending || idx < 0) return null;
            const hand = (state.hands && state.hands[idx]) || seatState.hand;
            if (!hand || !Array.isArray(hand) || hand.length === 0) return null;
            const vis = seatState.cardVisibility || {};
            if (!vis.showCards && !(seatState.isLocalPlayer && state.phase === 'action')) return null;

            const faceUp =
              (seatState.isLocalPlayer && (state.phase === 'action' || vis.showFaceUp)) ||
              !!vis.showFaceUp;

            if (seatState.isLocalPlayer) {
              return <LocalHand key={`hand-${idx}`} hand={hand} faceUp={faceUp || true} />;
            }
            return (
              <OpponentHand
                key={`hand-${idx}`}
                hand={hand}
                faceUp={faceUp}
                anchor={getHandAnchor(slot, false)}
              />
            );
          })}

        {/* Deal phase */}
        {state.phase === 'deal' && state.hands?.length > 0 && (
          <CasinoDealAnimation
            hands={state.hands}
            seatData={activeSeats.map((_, i) => seatData.find((s) => s.idx === i)).filter(Boolean)}
            allSeats={activeSeats}
            dealerIdx={state.dealerIdx}
            localPlayerID={player.playerID}
          />
        )}

        {/* Dealer reveal cards near center */}
        {(state.phase === 'dealer_reveal' || state.phase === 'dealer_selected') &&
          state.dealerRevealDeck?.length > 0 && (
            <DealerRevealFan
              deck={state.dealerRevealDeck}
              revealIdx={state.dealerRevealIdx}
              dealerSuit={state.dealerSuit}
            />
          )}
      </TableShell>

      <div className="cv2-controls-dock">
        <div className="cv2-phase-wrap">
          {globalUIState.pendingJoin?.active ? (
            <div className="cv2-phase-panel cv2-pending-join" role="status">
              <div style={{ textAlign: 'center', fontWeight: 700 }}>
                {globalUIState.pendingJoin.message}
              </div>
              <div style={{ textAlign: 'center', fontSize: '0.75rem', marginTop: 4, opacity: 0.85 }}>
                You are seated — play begins when the next eligible hand starts.
              </div>
            </div>
          ) : (
            <PhaseControls
              controls={globalUIState.controls}
              phase={state.phase}
              gameState={state}
              socket={socket}
              tableID={table.id}
              playerID={player.playerID}
              compact
            />
          )}
        </div>
      </div>

      {state.showDealerNoFaceCardNotice && !handResult.visible && (
        <div className="cv2-notice" role="alert">
          Dealer cannot knock without face cards (J, Q, K) — discard required.
        </div>
      )}

      {menuOpen && (
        <div className="cv2-menu-sheet" onClick={() => setMenuOpen(false)} role="presentation">
          <div
            className="cv2-menu-panel"
            role="dialog"
            aria-label="Table menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, marginBottom: 12, color: 'var(--cv2-gold-bright)' }}>
              Table menu
            </div>
            <button
              type="button"
              className="cv2-btn ghost"
              style={{ width: '100%', marginBottom: 8 }}
              onClick={() => {
                setMenuOpen(false);
                setUtilityPanel('history');
              }}
            >
              Hand History
            </button>
            <button
              type="button"
              className="cv2-btn ghost"
              style={{ width: '100%', marginBottom: 8 }}
              onClick={() => {
                setMenuOpen(false);
                setUtilityPanel('payment');
              }}
            >
              Payment Owed
            </button>
            {onTakeBreak && (
              <button
                type="button"
                className="cv2-btn"
                style={{ width: '100%', marginBottom: 8 }}
                onClick={() => {
                  setMenuOpen(false);
                  onTakeBreak();
                }}
              >
                Take a break
              </button>
            )}
            {onQuit && (
              <button
                type="button"
                className="cv2-btn danger"
                style={{ width: '100%', marginBottom: 8 }}
                onClick={() => {
                  setMenuOpen(false);
                  onQuit();
                }}
              >
                Quit table
              </button>
            )}
            <button
              type="button"
              className="cv2-btn ghost"
              style={{ width: '100%' }}
              onClick={() => setMenuOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <TableUtilityOverlay
        open={utilityPanel === 'history'}
        title="Hand History"
        onClose={() => setUtilityPanel(null)}
      >
        <HandHistoryPanel
          historyArr={historyArr}
          allSeats={allSeats}
          creditValue={creditValue}
        />
      </TableUtilityOverlay>

      <TableUtilityOverlay
        open={utilityPanel === 'payment'}
        title="Payment Owed"
        onClose={() => setUtilityPanel(null)}
      >
        <PaymentOwedPanel
          paymentHistory={paymentHistory}
          allSeats={allSeats}
          creditValue={creditValue}
          viewerPlayerID={player.playerID}
          tableID={table.id}
        />
      </TableUtilityOverlay>
    </div>
  );
}

function DealerRevealFan({ deck, revealIdx, dealerSuit }) {
  const maxIdx = revealIdx >= 0 ? revealIdx : 0;
  const card = deck[Math.min(maxIdx, deck.length - 1)];
  if (!card) return null;
  const isMatch = card.suit === dealerSuit;
  return (
    <div
      className="cv2-hand"
      style={{ left: '50%', top: '38%', zIndex: 20 }}
    >
      <PlayingCard card={card} faceUp size="center" />
      {isMatch && (
        <div className="cv2-status winner" style={{ position: 'absolute', top: '100%', marginTop: 6 }}>
          Dealer!
        </div>
      )}
    </div>
  );
}

function CasinoDealAnimation({ hands, seatData, allSeats, dealerIdx, localPlayerID }) {
  const [dealt, setDealt] = useState(() => new Set());

  useEffect(() => {
    setDealt(new Set());
    if (!hands?.length || dealerIdx == null) return undefined;
    const n = allSeats.length;
    const order = [];
    for (let round = 0; round < 2; round += 1) {
      for (let offset = 0; offset < n; offset += 1) {
        const seatIdx = (dealerIdx + 1 + offset) % n;
        order.push(seatIdx * 2 + round);
      }
    }
    let i = 0;
    const id = setInterval(() => {
      if (i >= order.length) {
        clearInterval(id);
        return;
      }
      const key = order[i];
      i += 1;
      setDealt((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    }, 400);
    return () => clearInterval(id);
  }, [hands, dealerIdx, allSeats.length]);

  return (
    <>
      {hands.map((hand, seatIdx) => {
        if (!hand?.length) return null;
        const meta = seatData[seatIdx];
        if (!meta) return null;
        const isLocal = allSeats[seatIdx]?.playerID === localPlayerID;
        const visible = hand.filter((_, cardIdx) => dealt.has(seatIdx * 2 + cardIdx));
        if (!visible.length) return null;
        if (isLocal) {
          return (
            <LocalHand
              key={`deal-${seatIdx}`}
              hand={visible}
              faceUp
              dealKeys={new Set(visible.map((_, i) => i))}
            />
          );
        }
        return (
          <OpponentHand
            key={`deal-${seatIdx}`}
            hand={visible}
            faceUp={false}
            anchor={getHandAnchor(meta.slot, false)}
            dealKeys={new Set(visible.map((_, i) => i))}
          />
        );
      })}
    </>
  );
}
